import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Flame, Trophy, TrendingUp, Calendar, Award, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import XPProgressBar from '../components/XPProgressBar';
import LevelBadge from '../components/LevelBadge';
import AchievementUnlock from '../components/AchievementUnlock';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = ({ userId }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [unlockedAchievement, setUnlockedAchievement] = useState(null);
  const [upgradeForm, setUpgradeForm] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    acceptPrivacy: false,
    acceptTerms: false,
    readGuidelines: false
  });

  useEffect(() => {
    fetchProfile();
    fetchAchievements();
    checkStreak();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/${userId}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchAchievements = async () => {
    try {
      const response = await axios.get(`${API}/achievements`);
      setAchievements(response.data);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
  };

  const checkStreak = async () => {
    try {
      const response = await axios.post(`${API}/profile/${userId}/check-streak`);
      if (response.data.streak_continued && response.data.xp_earned > 0) {
        toast.success(`🔥 Streak continued! +${response.data.xp_earned} XP`, {
          description: `You're on a ${response.data.current_streak} day streak!`
        });
        fetchProfile();
      }
    } catch (error) {
      console.error('Error checking streak:', error);
    }
  };

  const handleUpgrade = async (e) => {
    e.preventDefault();

    if (upgradeForm.password !== upgradeForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (upgradeForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!upgradeForm.acceptPrivacy || !upgradeForm.acceptTerms) {
      toast.error('Please accept the Privacy Policy and Terms of Use');
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/upgrade`, {
        uuid: profile.uuid,
        email: upgradeForm.email,
        username: upgradeForm.username,
        password: upgradeForm.password
      });

      // Save consent to backend
      await axios.put(`${API}/legal/consent/${userId}`, {
        privacy_policy_accepted: upgradeForm.acceptPrivacy,
        terms_accepted: upgradeForm.acceptTerms,
        community_guidelines_read: upgradeForm.readGuidelines
      });

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('username', upgradeForm.username); // Store username locally
      toast.success(`Welcome, ${upgradeForm.username}! Your account is now created.`);
      setShowUpgradeModal(false);
      
      // Refresh profile to show new username
      await fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
  };

  const isAchievementUnlocked = (achievementId) => {
    return profile?.achievements?.includes(achievementId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        <Button
          onClick={() => navigate('/')}
          className="mb-6 bg-black text-[#FFD800] font-bold rounded-full px-6 py-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)] transition-all"
          data-testid="back-to-dashboard"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="bg-ssdp-teal border-4 border-ssdp-navy rounded-3xl p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <LevelBadge level={profile.level} size="lg" />
                <div>
                  <h1 className="text-4xl font-black text-white uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {profile.username}
                  </h1>
                  {profile.is_anonymous && (
                    <p className="text-black/70 text-sm font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      Anonymous User • Progress Saved Locally
                    </p>
                  )}
                </div>
              </div>
              {profile.is_anonymous && (
                <Button
                  onClick={() => setShowUpgradeModal(true)}
                  className="bg-black text-[#FFD800] font-black px-6 py-3 rounded-xl hover:bg-black/90 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] uppercase"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid="upgrade-account-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create Account
                </Button>
              )}
            </div>

            <XPProgressBar currentXP={profile.xp} level={profile.level} />

            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-white border-3 border-ssdp-navy rounded-xl p-4 text-center shadow-[4px_4px_0px_0px_rgba(30,58,89,1)]">
                <Flame className="w-8 h-8 mx-auto mb-2 text-[#FF3B30]" strokeWidth={3} />
                <div className="text-3xl font-black text-black" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {profile.current_streak}
                </div>
                <div className="text-sm text-black/70 font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  Day Streak
                </div>
              </div>

              <div className="bg-white border-3 border-ssdp-navy rounded-xl p-4 text-center shadow-[4px_4px_0px_0px_rgba(30,58,89,1)]">
                <Trophy className="w-8 h-8 mx-auto mb-2 text-[#00E5FF]" strokeWidth={3} />
                <div className="text-3xl font-black text-black" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {profile.completed_scenarios?.length || 0}
                </div>
                <div className="text-sm text-black/70 font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  Completed
                </div>
              </div>

              <div className="bg-white border-3 border-ssdp-navy rounded-xl p-4 text-center shadow-[4px_4px_0px_0px_rgba(30,58,89,1)]">
                <Award className="w-8 h-8 mx-auto mb-2 text-[#FFD800]" strokeWidth={3} />
                <div className="text-3xl font-black text-black" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {profile.achievements?.length || 0}
                </div>
                <div className="text-sm text-black/70 font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  Achievements
                </div>
              </div>
            </div>
          </div>

          {profile.is_anonymous && (
            <div className="bg-ssdp-purple border-4 border-ssdp-navy rounded-2xl p-6 shadow-[6px_6px_0px_0px_rgba(30,58,89,1)]">
              <div className="flex items-start gap-3">
                <Sparkles className="w-8 h-8 text-black flex-shrink-0" strokeWidth={3} />
                <div>
                  <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    Unlock More Features!
                  </h3>
                  <p className="text-black mb-4 leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    Create a free account to:
                  </p>
                  <ul className="space-y-2 mb-4" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    <li className="flex items-center gap-2 text-black">
                      <span className="w-2 h-2 bg-black rounded-full"></span>
                      Access your progress from any device
                    </li>
                    <li className="flex items-center gap-2 text-black">
                      <span className="w-2 h-2 bg-black rounded-full"></span>
                      Secure your XP, levels, and achievements
                    </li>
                    <li className="flex items-center gap-2 text-black">
                      <span className="w-2 h-2 bg-black rounded-full"></span>
                      Never lose your learning progress
                    </li>
                  </ul>
                  <Button
                    onClick={() => setShowUpgradeModal(true)}
                    className="bg-black text-[#00E5FF] font-black px-6 py-3 rounded-xl hover:bg-black/90 uppercase"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                    data-testid="upgrade-cta-button"
                  >
                    Create Free Account
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white border-4 border-ssdp-navy rounded-3xl p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <h2 className="text-3xl font-black text-white uppercase tracking-tight mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              🏆 Achievements
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {achievements.map((achievement) => {
                const unlocked = isAchievementUnlocked(achievement.id);
                return (
                  <motion.div
                    key={achievement.id}
                    whileHover={{ scale: unlocked ? 1.02 : 1 }}
                    className={`border-3 border-ssdp-navy rounded-xl p-4 transition-all ${
                      unlocked
                        ? 'bg-ssdp-teal shadow-[4px_4px_0px_0px_rgba(30,58,89,1)]'
                        : 'bg-gray-100 opacity-50'
                    }`}
                    data-testid={`achievement-${achievement.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-4xl">{achievement.icon}</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-black" style={{ fontFamily: 'Figtree, sans-serif' }}>
                          {achievement.title}
                        </h3>
                        <p className="text-sm text-black/70 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
                          {achievement.description}
                        </p>
                        {unlocked && (
                          <span className="inline-block bg-black text-[#FFD800] px-3 py-1 rounded-full text-xs font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                            +{achievement.xp_bonus} XP
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>

      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-white border-4 border-ssdp-navy rounded-3xl shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-3xl font-black text-white uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Create Your Account
            </DialogTitle>
            <DialogDescription className="text-black/70" style={{ fontFamily: 'Figtree, sans-serif' }}>
              Secure your progress and access it from any device
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpgrade} className="space-y-4" data-testid="upgrade-form">
            <div>
              <Label htmlFor="email" className="text-black font-bold" style={{ fontFamily: 'Figtree, sans-serif' }}>Email</Label>
              <Input
                id="email"
                type="email"
                value={upgradeForm.email}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, email: e.target.value })}
                required
                className="border-3 border-ssdp-navy rounded-xl mt-1"
                data-testid="upgrade-email-input"
              />
            </div>

            <div>
              <Label htmlFor="username" className="text-black font-bold" style={{ fontFamily: 'Figtree, sans-serif' }}>Username</Label>
              <Input
                id="username"
                type="text"
                value={upgradeForm.username}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, username: e.target.value })}
                required
                className="border-3 border-ssdp-navy rounded-xl mt-1"
                data-testid="upgrade-username-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-black font-bold" style={{ fontFamily: 'Figtree, sans-serif' }}>Password</Label>
              <Input
                id="password"
                type="password"
                value={upgradeForm.password}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, password: e.target.value })}
                required
                minLength={6}
                className="border-3 border-ssdp-navy rounded-xl mt-1"
                data-testid="upgrade-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-black font-bold" style={{ fontFamily: 'Figtree, sans-serif' }}>Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={upgradeForm.confirmPassword}
                onChange={(e) => setUpgradeForm({ ...upgradeForm, confirmPassword: e.target.value })}
                required
                className="border-3 border-ssdp-navy rounded-xl mt-1"
                data-testid="upgrade-confirm-password-input"
              />
            </div>

            {/* Legal Consent Section */}
            <div className="space-y-3 pt-4 border-t-2 border-gray-200">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="acceptPrivacy"
                  checked={upgradeForm.acceptPrivacy}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, acceptPrivacy: e.target.checked })}
                  required
                  className="w-5 h-5 text-ssdp-teal rounded border-ssdp-navy mt-0.5"
                  data-testid="accept-privacy-checkbox"
                />
                <label htmlFor="acceptPrivacy" className="text-sm text-gray-700">
                  I have read and accept the{' '}
                  <a href="/legal/privacy" target="_blank" className="text-ssdp-teal hover:underline font-semibold">
                    Privacy Policy
                  </a>
                  {' '}<span className="text-red-600">*</span>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={upgradeForm.acceptTerms}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, acceptTerms: e.target.checked })}
                  required
                  className="w-5 h-5 text-ssdp-teal rounded border-ssdp-navy mt-0.5"
                  data-testid="accept-terms-checkbox"
                />
                <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a href="/legal/terms" target="_blank" className="text-ssdp-teal hover:underline font-semibold">
                    Terms of Use
                  </a>
                  {' '}<span className="text-red-600">*</span>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="readGuidelines"
                  checked={upgradeForm.readGuidelines}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, readGuidelines: e.target.checked })}
                  className="w-5 h-5 text-ssdp-teal rounded border-ssdp-navy mt-0.5"
                  data-testid="read-guidelines-checkbox"
                />
                <label htmlFor="readGuidelines" className="text-sm text-gray-700">
                  I have read the{' '}
                  <a href="/legal/community-guidelines" target="_blank" className="text-ssdp-teal hover:underline font-semibold">
                    Community Guidelines
                  </a>
                </label>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                <span className="text-red-600">*</span> Required to create an account
              </p>
            </div>

            <Button
              type="submit"
              disabled={!upgradeForm.acceptPrivacy || !upgradeForm.acceptTerms}
              className="w-full bg-ssdp-teal text-black font-black py-3 rounded-xl border-3 border-ssdp-navy hover:bg-ssdp-teal/90 shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="submit-upgrade-button"
            >
              Create Account
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {unlockedAchievement && (
          <AchievementUnlock
            achievement={unlockedAchievement}
            onClose={() => setUnlockedAchievement(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfilePage;